import React, { useState, useCallback, useRef } from 'react';
import { useQuery } from 'react-query';

import { withStyles } from '@material-ui/core/styles';
import MuiPaper from '@material-ui/core/Paper';
import MuiGrid from '@material-ui/core/Grid';
import MuiCircularProgress from '@material-ui/core/CircularProgress';

import ViewWrapper from 'views/ViewWrapper';

import SideBar from './ViewComponents/SideBar';
import MainContent from './ViewComponents/MainContent';
import Header from './ViewComponents/Header';
import { formatDate } from './RecoupAccounts';
import * as API from 'api';

const RecoupAccounts = API.RecoupAccounts();
const LEDGER_PAGE_SIZE = 250;

const styles = theme => ({
  paper: {
    overflow: 'hidden',
    padding: theme.spacing(2),
  },
  loadWrapper: {
    display: 'flex',
    justifyContent: 'center',
    margin: '1rem 0',
  },
});

function RecoupmentAccount({ ...props }) {
  const {
    classes,
    match: {
      params: { id },
    },
  } = props;

  //Used as a debounce method to prevent the query from activating every time someone types.
  const [queryEnabled, setQueryEnabled] = useState(true);
  const queryTimeoutTrackerRef = useRef();

  const [ledgerParams, setLedgerParams] = useState({
    page: 0,
    pageSize: 50,
    amountFilter: '-10',
    amountOperator: '<',
  });

  const {
    data: ledgerData,
    refetch: refetchLedgers,
    isFetching: isFetchingLedgers,
  } = useQuery(
    ['recoup-account-ledgers', ledgerParams],
    async () => {
      const ledgerResults = await RecoupAccounts.getLedgers(id, ledgerParams);
      const { total } = ledgerResults;

      const totalPages = Math.ceil(total / ledgerParams.pageSize);
      const hasNextPage = ledgerParams.page < totalPages - 1;
      const hasPreviousPage = ledgerParams.page > 0;

      return { totalPages, ledgerRows: ledgerResults.results, hasNextPage, hasPreviousPage };
    },
    {
      enabled: queryEnabled,
      keepPreviousData: true,
    }
  );

  const {
    data: account,
    isFetchingAccount,
    refetch: refetchAccount,
  } = useQuery(['recoup-account', id], () => RecoupAccounts.getById(id));

  const formattedAsOf = formatDate(new Date().getTime());
  const refreshPageQueries = useCallback(() => {
    refetchAccount();
    refetchLedgers();
  }, [refetchAccount, refetchLedgers]);

  const fetchPage = useCallback(
    (adjustor = 1) => {
      setLedgerParams(currentParams => ({ ...currentParams, page: currentParams.page + adjustor }));
    },
    [setLedgerParams]
  );

  const changeFilter = useCallback(
    e => {
      const {
        target: { name, value },
      } = e;

      clearTimeout(queryTimeoutTrackerRef.current);
      setQueryEnabled(false);
      setLedgerParams(currentParams => ({ ...currentParams, [name]: value }));

      queryTimeoutTrackerRef.current = setTimeout(() => setQueryEnabled(true), 500);
    },
    [setLedgerParams, setQueryEnabled]
  );

  if (isFetchingAccount || !ledgerData || !account)
    return (
      <ViewWrapper>
        <MuiPaper>
          <MuiCircularProgress className={classes.loadWrapper} />
        </MuiPaper>
      </ViewWrapper>
    );
  const { ledgerRows, hasNextPage, hasPreviousPage } = ledgerData;

  return (
    <ViewWrapper>
      <MuiPaper>
        <MuiGrid container spacing={1}>
          <MuiGrid item xs={12}>
            <Header labelName={account.name} asOfDate={formattedAsOf} />
          </MuiGrid>
          <MuiGrid item xs={2}>
            <SideBar account={account} />
          </MuiGrid>
          <MuiGrid item xs={10}>
            <MainContent
              account={account}
              ledgers={ledgerRows}
              refetch={refreshPageQueries}
              hasNextLedgerRowPage={hasNextPage && isFetchingLedgers === false}
              hasPreviousLedgerRowPage={hasPreviousPage && isFetchingLedgers === false}
              adjustLedgerPage={fetchPage}
              filterOperator={ledgerParams.amountOperator}
              filterNumber={ledgerParams.amountFilter}
              onFilterChange={changeFilter}
            />
          </MuiGrid>
        </MuiGrid>
      </MuiPaper>
    </ViewWrapper>
  );
}

export default withStyles(styles)(RecoupmentAccount);
